import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { XeroService } from '../../../../shared/services/xero.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ConfigService } from '../../../../shared/services/config.service';
import { NewXeroTaxRateDialogComponent } from './new-tax-rate-dialog/new-xero-tax-rate-dialog.component';
import { TaxRate } from '../../../../models/tax-rate';
import { TaxRate as XeroTaxRate } from '../../../../models/xero/tax-rate';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton } from '@angular/material/button';
import { FlexModule } from '@angular/flex-layout';
import { NgIf, NgFor } from '@angular/common';

@Component({
    selector: 'tax-mappings',
    templateUrl: './tax-mappings.component.html',
    providers: [XeroService],
    standalone: true,
    imports: [NgIf, FlexModule, ReactiveFormsModule, NgFor, MatButton, MatProgressSpinner]
})
export class TaxMappingsComponent implements OnInit, OnDestroy {
  xeroTaxRates: XeroTaxRate[];
  localTaxRates: any[];

  private ngUnsubscribe = new Subject<void>();
  form: UntypedFormGroup;

  @Input() set taxRatesForm(value: UntypedFormGroup) {
    this.form = value;
  }

  constructor(
    private xeroService: XeroService,
    private dialog: MatDialog,
    private configService: ConfigService,
    private formBuilder: UntypedFormBuilder
  ) {}

  ngOnInit() {
    this.configService.taxRates
      .pipe(
        filter((taxRates) => !!taxRates),
        takeUntil(this.ngUnsubscribe),
        tap((taxRates) => this.setLocalTaxRates(taxRates)),
        mergeMap(() => this.xeroService.getXeroTaxRates())
      )
      .subscribe((xeroTaxRates: XeroTaxRate[]) =>
        this.setXeroTaxRates(xeroTaxRates)
      );
  }

  private setXeroTaxRates(xeroTaxRates: XeroTaxRate[]) {
    this.xeroTaxRates = xeroTaxRates;
    if (xeroTaxRates.length) {
      this.setTaxRateFormControls(xeroTaxRates);
    }
  }

  private setLocalTaxRates(taxRates) {
    this.setupFormControls(taxRates);
    this.localTaxRates = taxRates;
  }

  createNewTaxRate(taxRate: TaxRate) {
    this.dialog
      .open(NewXeroTaxRateDialogComponent, {
        data: {
          taxRate: taxRate
        }
      })
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((val) => {
        if (val) {
          this.xeroTaxRates.push(val);
          this.form
            .get(val.config_sequence.toString())
            .get('xero_reference')
            .patchValue(val.tax_type);
          this.form
            .get(val.config_sequence.toString())
            .get('xero_tax_rate')
            .patchValue(val);
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private setupFormControls(taxRates: TaxRate[]) {
    taxRates.forEach((taxRate) => {
      if (
        taxRate.config_sequence === undefined ||
        taxRate.config_sequence === null
      ) {
        throw 'Tax rates not set up in config correctly - please run tax rate seed.';
      }
      this.form.addControl(
        taxRate.config_sequence.toString(),
        this.formBuilder.group({
          name: this.formBuilder.control(taxRate.name),
          sequence: this.formBuilder.control(taxRate.config_sequence),
          xero_reference: this.formBuilder.control(
            taxRate.accounts_reference,
            Validators.required
          ),
          xero_tax_rate: this.formBuilder.control(null, Validators.required),
          type: this.formBuilder.control(taxRate.type)
        })
      );
    });
  }

  private setTaxRateFormControls(xeroTaxRates: XeroTaxRate[]) {
    xeroTaxRates.forEach((rate) => {
      this.form
        .get(rate.config_sequence.toString())
        .get('xero_reference')
        .patchValue(rate.tax_type);
      this.form
        .get(rate.config_sequence.toString())
        .get('xero_tax_rate')
        .patchValue(rate);
    });
  }
}
